/**
 * Locale-aware dates and numbers (refonte, D-119).
 *
 * Every date and number on screen goes through these helpers, in one locale.
 * Before, each page called `toLocaleDateString(undefined, …)`, which formats in
 * the *browser's* locale: an English UI on a French machine showed
 * "LUNDI 12 OCTOBRE" next to "09/08/2026". The helpers default to the UI
 * locale (`currentLocale()`, D-145), so dates follow the copy's language.
 *
 * Recent items read as relative time ("4 min", "Yesterday"); older ones as a
 * short date in the same locale. Never mix the two styles in one list.
 * `formatWhen` is the redesign's one date formatter; screens move to it as
 * they are redesigned, and the older helpers go once nothing uses them.
 */

// The UI locale lives with the i18n layer (D-145); every helper defaults to it.
import i18n from '../i18n';
import { currentLocale, type AppLocale } from '../i18n/locale';

export type { AppLocale };

/** en-GB, not en-US: day-month order and a 24-hour clock, as in the design. */
const BCP47: Record<AppLocale, string> = { en: 'en-GB', fr: 'fr-FR' };

type DateInput = Date | string | number;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const RELATIVE_WORDS: Record<
  AppLocale,
  { justNow: string; yesterday: string; ago: (span: string) => string; at: (day: string, time: string) => string }
> = {
  en: { justNow: 'just now', yesterday: 'Yesterday', ago: (s) => `${s} ago`, at: (d, t) => `${d}, ${t}` },
  fr: { justNow: 'à l’instant', yesterday: 'Hier', ago: (s) => `il y a ${s}`, at: (d, t) => `${d}, ${t}` },
};

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

/** What an unreadable date shows instead of a thrown RangeError taking the page down. */
export const INVALID_DATE_TEXT = '—';

const isValid = (date: Date): boolean => !Number.isNaN(date.getTime());

function formatWith(locale: AppLocale, options: Intl.DateTimeFormatOptions, value: DateInput): string {
  const date = toDate(value);
  return isValid(date) ? dateFormatter(locale, options).format(date) : INVALID_DATE_TEXT;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// Intl formatters are costly to build and these run per row: cache them.
const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function dateFormatter(locale: AppLocale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d|${locale}|${JSON.stringify(options)}`;
  let formatter = cache.get(key) as Intl.DateTimeFormat | undefined;
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(BCP47[locale], options);
    cache.set(key, formatter);
  }
  return formatter;
}

function numberFormatter(locale: AppLocale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `n|${locale}|${JSON.stringify(options)}`;
  let formatter = cache.get(key) as Intl.NumberFormat | undefined;
  if (!formatter) {
    formatter = new Intl.NumberFormat(BCP47[locale], options);
    cache.set(key, formatter);
  }
  return formatter;
}

// --- numbers --------------------------------------------------------------------------

/** `1,284` in English, `1 284` in French. */
export function formatNumber(value: number, locale: AppLocale = currentLocale()): string {
  return numberFormatter(locale, { maximumFractionDigits: 1 }).format(value);
}

/** A ratio (0.963) as a percentage: `96.3%` / `96,3 %`. Whole numbers drop the decimal. */
export function formatPercent(ratio: number, locale: AppLocale = currentLocale(), maxDigits = 1): string {
  return numberFormatter(locale, { style: 'percent', maximumFractionDigits: maxDigits }).format(ratio);
}

/**
 * `12.4K` / `12,4 k` — for metric columns where width matters more than
 * precision. English keeps the uppercase `K`/`M` the product already showed;
 * en-GB would render `12.4k` and `1.2m`.
 */
export function formatCompact(value: number, locale: AppLocale = currentLocale()): string {
  const options: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 };
  if (locale === 'en') return new Intl.NumberFormat('en', options).format(value);
  return numberFormatter(locale, options).format(value);
}

/** `200,000 TND` in French, `200.000 TND` in English: TND counts millimes, so three decimals (GLOSSARY-FR §3). */
export function formatAmount(amount: number, currency: string, locale: AppLocale = currentLocale()): string {
  const digits = currency === 'TND' ? 3 : 2;
  return `${numberFormatter(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount)} ${currency}`;
}

/**
 * A plan's price (D-183, Q7-A): `99 TND` when it is round, otherwise as
 * `formatAmount` (`89,500 TND`). Invoices keep `formatAmount`'s millimes.
 */
export function formatPlanPrice(amount: number, currency: string, locale: AppLocale = currentLocale()): string {
  if (!Number.isInteger(amount)) return formatAmount(amount, currency, locale);
  return `${numberFormatter(locale, { maximumFractionDigits: 0 }).format(amount)} ${currency}`;
}

// --- dates ----------------------------------------------------------------------------

/** `26 Oct 2026` / `26 oct. 2026` for a calendar day stored at midnight UTC (an invoice's due date), read in UTC. */
export function formatCalendarDate(value: DateInput, locale: AppLocale = currentLocale()): string {
  return formatWith(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }, value);
}

/** `14 Sept – 14 Oct` / `14 sept. – 14 oct.`: a billing period, its calendar days read in UTC like `formatCalendarDate`. */
export function formatCalendarPeriod(first: DateInput, last: DateInput, locale: AppLocale = currentLocale()): string {
  const day = (value: DateInput) => formatWith(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }, value);
  return `${day(first)} – ${day(last)}`;
}

/** `14 Aug 2026`. */
export function formatDate(value: DateInput, locale: AppLocale = currentLocale()): string {
  return formatWith(locale, { day: 'numeric', month: 'short', year: 'numeric' }, value);
}

/** `9 Aug` this year, `9 Aug 2025` otherwise. */
export function formatShortDate(value: DateInput, now: Date = new Date(), locale: AppLocale = currentLocale()): string {
  const date = toDate(value);
  if (date.getFullYear() !== now.getFullYear()) return formatDate(date, locale);
  return formatWith(locale, { day: 'numeric', month: 'short' }, date);
}

/** `Monday 12 October` — the dashboard eyebrow. */
export function formatLongDate(value: DateInput, locale: AppLocale = currentLocale()): string {
  return formatWith(locale, { weekday: 'long', day: 'numeric', month: 'long' }, value);
}

/** `Tue 14 Oct` — a weekday with its date, for schedules. */
export function formatDayDate(value: DateInput, locale: AppLocale = currentLocale()): string {
  return formatWith(locale, { weekday: 'short', day: 'numeric', month: 'short' }, value);
}

/** `Tue` / `mar.`; on the given zone's clock when one is given (a calendar's day label reads in UTC). */
export function formatWeekday(value: DateInput, locale: AppLocale = currentLocale(), timeZone?: string): string {
  return formatWith(locale, { weekday: 'short', timeZone }, value);
}

/** `18:00` — a 24-hour clock in both locales. */
export function formatTime(value: DateInput, locale: AppLocale = currentLocale()): string {
  return formatWith(locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, value);
}

/** `14 Aug 2026, 18:00`. */
export function formatDateTime(value: DateInput, locale: AppLocale = currentLocale()): string {
  if (!isValid(toDate(value))) return INVALID_DATE_TEXT;
  return RELATIVE_WORDS[locale].at(formatDate(value, locale), formatTime(value, locale));
}

export interface RelativeOptions {
  /** `4 min ago` rather than `4 min` — for sentences; bare spans suit time columns. */
  suffix?: boolean;
  /** Older than today: append the clock time (`Yesterday, 14:28`). */
  withTime?: boolean;
  /** Mid-sentence (`Edited yesterday`): the day word in lower case. */
  inSentence?: boolean;
  now?: Date;
  locale?: AppLocale;
}

/**
 * Relative time for recent items: `just now`, `4 min`, `2 h` today, then
 * `Yesterday`, a weekday within the week (`Mon`), and a short date beyond.
 * Future instants (a scheduled post) are never "ago": they get the date.
 */
export function formatRelative(value: DateInput, options: RelativeOptions = {}): string {
  const { suffix = false, withTime = false, inSentence = false, now = new Date(), locale = currentLocale() } = options;
  const date = toDate(value);
  const words = RELATIVE_WORDS[locale];
  const elapsed = now.getTime() - date.getTime();
  const span = (text: string) => (suffix ? words.ago(text) : text);
  const time = (day: string) => (withTime ? words.at(day, formatTime(date, locale)) : day);

  // A server a few seconds ahead of this clock still wrote "just now", not a date.
  if (elapsed < -MINUTE) return withTime ? formatDateTime(date, locale) : formatShortDate(date, now, locale);
  if (elapsed < MINUTE) return words.justNow;
  if (elapsed < HOUR) return span(`${Math.floor(elapsed / MINUTE)} min`);

  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / DAY);
  if (daysAgo === 0) return span(`${Math.floor(elapsed / HOUR)} h`);
  if (daysAgo === 1) return time(inSentence ? words.yesterday.toLocaleLowerCase(locale) : words.yesterday);
  if (daysAgo < 7) return time(formatWeekday(date, locale));
  return time(formatShortDate(date, now, locale));
}

// --- formatWhen (redesign) ------------------------------------------------------------

/** Something that already happened: judged and shown in the reader's (browser's) zone unless told otherwise. */
export interface PastWhenOptions {
  locale?: AppLocale;
  timeZone?: string;
  scheduled?: false;
  now?: Date;
}

/**
 * A planned date (post, story, campaign): always the full date with the year
 * and the zone it was scheduled in, never the reader's zone, so two people in
 * different zones read the same "10:00 · Europe/Paris".
 */
export interface ScheduledWhenOptions {
  locale?: AppLocale;
  scheduled: true;
  /** The scheduling zone (IANA), required: a planned time never falls back to the browser's zone. */
  timeZone: string;
  /** False when the zone is already printed next to it (the composer's picker, GLOSSARY-FR §3). */
  showZone?: boolean;
  now?: Date;
}

export type WhenOptions = PastWhenOptions | ScheduledWhenOptions;

/** `2026-10-23` in the given zone, so "today" and "this year" are judged where the reader is. */
function calendarDay(date: Date, timeZone: string | undefined): string {
  const parts = dateFormatter('en', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * The redesign's one date formatter (GLOSSARY-FR §3, SCREENS.md):
 * under an hour `il y a 20 min` · today `14:06` · this year `12 oct.` ·
 * another year `jeu. 14 août 2025 · 21:05` ·
 * anything scheduled `lun. 15 mars 2027 · 10:00 · Europe/Paris`.
 */
export function formatWhen(value: DateInput, options: WhenOptions = {}): string {
  const { locale = currentLocale(), timeZone, now = new Date() } = options;
  const date = toDate(value);
  if (!isValid(date)) return INVALID_DATE_TEXT;

  const { date: day, time } = scheduledParts(date, locale, timeZone);
  const full = `${day} · ${time}`;
  if (options.scheduled) return options.showZone === false ? full : `${full} · ${options.timeZone}`;

  const kind = whenKind(date, { timeZone, now });
  if (kind === 'ago') {
    const minutesAgo = Math.round((now.getTime() - date.getTime()) / MINUTE);
    return new Intl.RelativeTimeFormat(BCP47[locale], { numeric: 'always', style: 'short' }).format(-Math.max(minutesAgo, 1), 'minute');
  }
  if (kind === 'today') return time;
  if (calendarDay(date, timeZone).slice(0, 4) === calendarDay(now, timeZone).slice(0, 4)) {
    return dateFormatter(locale, { timeZone, day: 'numeric', month: 'short' }).format(date);
  }
  return full;
}

/**
 * Which of `formatWhen`'s shapes a past date takes, so a sentence around it can
 * read right: "Modifiée il y a 20 min", "Modifiée à 14:06", "Modifiée le 12 oct.".
 */
export function whenKind(value: DateInput, { timeZone, now = new Date() }: { timeZone?: string; now?: Date } = {}): 'ago' | 'today' | 'date' {
  const date = toDate(value);
  const minutesAgo = Math.round((now.getTime() - date.getTime()) / MINUTE);
  if (minutesAgo >= 0 && minutesAgo < 60) return 'ago';
  return calendarDay(date, timeZone) === calendarDay(now, timeZone) ? 'today' : 'date';
}

/** How long until a planned instant, whole and in words: "dans 143 jours" with its parts. */
export interface Countdown {
  /** `dans 143 jours` / `in 143 days`. */
  text: string;
  /** `dans` / `in`. */
  prefix: string;
  /** `143 jours` / `143 days`: the hero writes it under its own "part dans". */
  span: string;
}

/** Up to this far ahead the countdown counts hours and minutes; beyond it, days. */
const COUNTDOWN_HOURS_UNTIL = 48 * HOUR;

/**
 * Time left before a planned instant (Murmura M1, PROPOSALS.md): under an hour
 * `dans 25 min` (never less than 1 min), under 48 h `dans 10 h 16 min` (or
 * `dans 10 h` on the hour), then whole days `dans 143 jours`. Every unit is
 * rounded down. A past instant, one less than a minute away, or an unreadable
 * date has no countdown: null. Computed on the reader's clock, like any duration.
 */
export function formatCountdown(value: DateInput, { now = new Date(), locale = currentLocale() }: { now?: Date; locale?: AppLocale } = {}): Countdown | null {
  const date = toDate(value);
  if (!isValid(date)) return null;
  const left = date.getTime() - now.getTime();
  if (left < MINUTE) return null;
  const t = i18n.getFixedT(locale);
  let span: string;
  if (left < HOUR) {
    span = t('duration.minutes', { count: Math.max(1, Math.floor(left / MINUTE)) });
  } else if (left < COUNTDOWN_HOURS_UNTIL) {
    const hours = Math.floor(left / HOUR);
    const minutes = Math.floor((left % HOUR) / MINUTE);
    span = minutes === 0 ? t('countdown.hours', { count: hours }) : t('countdown.hoursMinutes', { hours, minutes });
  } else {
    span = t('duration.days', { count: Math.floor(left / DAY) });
  }
  return { text: t('countdown.text', { span }), prefix: t('countdown.in'), span };
}

function scheduledParts(date: Date, locale: AppLocale, timeZone: string | undefined): { date: string; time: string } {
  return {
    date: dateFormatter(locale, { timeZone, weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }).format(date),
    time: dateFormatter(locale, { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date),
  };
}

/**
 * The days a set of planned dates spans, in the scheduling zone and always
 * with the year: `15 mars 2027`, `15–28 mars 2027`, `28 mars – 3 avr. 2027`
 * (a campaign's dates, D-146).
 */
export function formatDayRange(
  first: DateInput,
  last: DateInput,
  { locale = currentLocale(), timeZone }: { locale?: AppLocale; timeZone: string },
): string {
  const from = toDate(first);
  const to = toDate(last);
  if (!isValid(from) || !isValid(to)) return INVALID_DATE_TEXT;
  return dateFormatter(locale, { timeZone, day: 'numeric', month: 'short', year: 'numeric' }).formatRange(from, to);
}

/** `novembre 2026` / `November 2026`: the calendar's month (Murmura M4, D-188), on the given zone's clock. */
export function formatMonth(
  value: DateInput,
  { locale = currentLocale(), timeZone }: { locale?: AppLocale; timeZone?: string } = {},
): string {
  const date = toDate(value);
  if (!isValid(date)) return INVALID_DATE_TEXT;
  return dateFormatter(locale, { timeZone, month: 'long', year: 'numeric' }).format(date);
}

/**
 * `mars 2027` or `oct.` / `Mar 2027` or `Oct`: a month in short, with its year
 * or without (the phone agenda's date column, Murmura M4, D-188), on the given zone's clock.
 */
export function formatShortMonth(
  value: DateInput,
  { locale = currentLocale(), timeZone, withYear = false }: { locale?: AppLocale; timeZone?: string; withYear?: boolean } = {},
): string {
  const date = toDate(value);
  if (!isValid(date)) return INVALID_DATE_TEXT;
  return dateFormatter(locale, withYear ? { timeZone, month: 'short', year: 'numeric' } : { timeZone, month: 'short' }).format(date);
}

/** `15 mars 2027` / `15 March 2027`: a planned day without its weekday or time (the calendar's "Aller au …", D-188), on the given zone's clock. */
export function formatPlannedDay(value: DateInput, { locale = currentLocale(), timeZone }: { locale?: AppLocale; timeZone: string }): string {
  const date = toDate(value);
  if (!isValid(date)) return INVALID_DATE_TEXT;
  return dateFormatter(locale, { timeZone, day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/**
 * A scheduled date in pieces, for layouts that split it (the Posts list's date
 * column: `lun. 15 mars 2027` over `10:00 · Europe/Paris`; a story's title).
 * The pieces `formatWhen(…, { scheduled: true })` joins with " · ".
 */
export function formatScheduled(
  value: DateInput,
  { locale = currentLocale(), timeZone }: { locale?: AppLocale; timeZone: string },
): { date: string; time: string; zone: string } | null {
  const date = toDate(value);
  if (!isValid(date)) return null;
  return { ...scheduledParts(date, locale, timeZone), zone: timeZone };
}

/**
 * `15 mars 2027, 10:00` on the scheduling zone's clock: a planned time where
 * the weekday and the zone are already said nearby (the composer's preview, R19, R26).
 */
export function formatScheduledShort(
  value: DateInput,
  { locale = currentLocale(), timeZone }: { locale?: AppLocale; timeZone: string },
): string {
  const date = toDate(value);
  if (!isValid(date)) return INVALID_DATE_TEXT;
  const day = dateFormatter(locale, { timeZone, day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  const time = dateFormatter(locale, { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date);
  return `${day}, ${time}`;
}
